import i18n from "../i18n";
import { roundToDecimalPlace } from "./ArithmeticUtils/ArithmeticUtils";
import { formatGroupedNumber } from "./numberFormat";
import { isDisplayUnitsExtended } from "../global/displayUnitsFlag";
import { MAX_LAGS, MIN_LAGS } from "../global/lagLimits";

/**
 * Portfolio display/input units for Duration & Work (Gantt Options).
 *
 * Core invariant: every stored/POSTed value stays in core units
 * (Duration = days, Work = hours). This module owns the *display*
 * conversion — renderers, editor presets, filter comparisons and
 * parse-backs all go through here so the factors ("days per week",
 * "days per month", "hours per day") are applied consistently.
 *
 * Never convert with Bryntum's DateHelper: it uses calendar constants
 * (week = 7 days, day = 24 h), not working-time factors.
 *
 * The cache is written by portfolioActions (getDetails/updateDetails
 * success) — before any component consumes the details — and read
 * lazily by renderers, so pre-fetch rendering falls back to core units
 * (byte-identical to pre-feature output).
 */

const DURATION_UNITS = { DAY: "day", WEEK: "week", MONTH: "month" };
const WORK_UNITS = { HOUR: "hour", DAY: "day" };

const DEFAULTS = {
	durationUnit: DURATION_UNITS.DAY,
	workUnit: WORK_UNITS.HOUR,
	daysPerWeek: 5,
	daysPerMonth: 20,
	hoursPerDay: 8,
};

const cache = { ...DEFAULTS, labels: null };

// Unit labels resolve through i18n once per settings/language change —
// never per cell: the formatters run for every visible cell on every
// scroll/refresh pass of gantts that can hold thousands of rows.
const SHORT_SUFFIX_KEYS = { day: "d", week: "w", month: "mo", hour: "h", minute: "min" };

const resolveLabels = () => {
	const one = (unit) => i18n.t(`ganttUnits.${unit}`, unit);
	const many = (unit) => i18n.t(`ganttUnits.${unit}s`, `${unit}s`);
	const short = (unit) => {
		const s = SHORT_SUFFIX_KEYS[unit] || unit;
		return i18n.t(`ganttUnits.short.${s}`, s);
	};
	cache.labels = {};
	["day", "week", "month", "hour", "minute"].forEach((unit) => {
		cache.labels[unit] = { one: one(unit), many: many(unit), short: short(unit) };
	});
};

if (i18n?.on) {
	i18n.on("languageChanged", resolveLabels);
}

const labelsFor = (unit) => {
	if (!cache.labels) resolveLabels();
	return cache.labels[unit] || { one: unit, many: `${unit}s`, short: unit };
};

const toFiniteOrDefault = (value, fallback) => {
	const n = Number(value);
	return Number.isFinite(n) && n > 0 ? n : fallback;
};

/**
 * Feed the cache from the portfolio details payload
 * (`state.portfolio.details`). Missing/invalid values fall back to the
 * core-unit defaults.
 */
export const setDisplayUnitSettings = (details) => {
	const d = details || {};
	cache.durationUnit =
		DURATION_UNITS[d.durationDisplayUnit] || DEFAULTS.durationUnit;
	cache.workUnit = WORK_UNITS[d.workDisplayUnit] || DEFAULTS.workUnit;
	cache.daysPerWeek = toFiniteOrDefault(d.daysPerWeek, DEFAULTS.daysPerWeek);
	cache.daysPerMonth = toFiniteOrDefault(d.daysPerMonth, DEFAULTS.daysPerMonth);
	// The spec's "align with daily FTE" option: when the flag is on the
	// work conversion uses the portfolio's dailyFTE instead of the
	// dedicated hours-per-day constant.
	const hoursPerDay = d.hoursPerDayUseDailyFte ? d.dailyFTE : d.hoursPerDay;
	cache.hoursPerDay = toFiniteOrDefault(hoursPerDay, DEFAULTS.hoursPerDay);
	resolveLabels();
};

export const getDisplayUnitSettings = () => ({
	durationUnit: cache.durationUnit,
	workUnit: cache.workUnit,
	daysPerWeek: cache.daysPerWeek,
	daysPerMonth: cache.daysPerMonth,
	hoursPerDay: cache.hoursPerDay,
});

export const isDurationCore = (settings = cache) =>
	settings.durationUnit === DURATION_UNITS.DAY;
export const isWorkCore = (settings = cache) =>
	settings.workUnit === WORK_UNITS.HOUR;

/**
 * The extended contract applies when its flag is on and the portfolio
 * converts at least one of the two dimensions. A Day / Hour portfolio
 * keeps today's rendering byte for byte.
 */
export const isExtendedDisplay = (settings = cache) =>
	isDisplayUnitsExtended() && !(isDurationCore(settings) && isWorkCore(settings));

/** Days per active duration display unit. */
const durationFactor = (settings = cache) => {
	if (settings.durationUnit === DURATION_UNITS.WEEK) return settings.daysPerWeek;
	if (settings.durationUnit === DURATION_UNITS.MONTH) return settings.daysPerMonth;
	return 1;
};

/** Hours per active work display unit. */
const workFactor = (settings = cache) =>
	settings.workUnit === WORK_UNITS.DAY ? settings.hoursPerDay : 1;

const factorForDurationUnit = (unit, settings = cache) => {
	if (unit === DURATION_UNITS.WEEK) return settings.daysPerWeek;
	if (unit === DURATION_UNITS.MONTH) return settings.daysPerMonth;
	return 1;
};

export const toDisplayDuration = (days, settings = cache) =>
	days == null ? days : days / durationFactor(settings);

/**
 * @param magnitude value typed by the user
 * @param unit the unit Bryntum parsed ("day" | "week" | "month");
 *             omitted → the active display unit
 */
export const fromDisplayDuration = (magnitude, unit, settings = cache) =>
	magnitude == null
		? magnitude
		: magnitude * factorForDurationUnit(unit || settings.durationUnit, settings);

export const toDisplayWork = (hours, settings = cache) =>
	hours == null ? hours : hours / workFactor(settings);

/** @param unit "hour" | "day"; omitted → the active work display unit */
export const fromDisplayWork = (magnitude, unit, settings = cache) => {
	if (magnitude == null) return magnitude;
	const u = unit || settings.workUnit;
	return u === WORK_UNITS.DAY ? magnitude * settings.hoursPerDay : magnitude;
};

/**
 * The one precision every Duration/Work surface shares: cell renderers,
 * editor fields, editor bounds and filter comparisons.
 *
 * Renderer and editor must agree: a cell showing "0.23 months" that
 * opened an editor on "0.2" left the user editing a number the app had
 * never displayed (see presetUnitField for what that then committed).
 * Trailing zeros stay trimmed ("2.5", "3" — never "3.00"), so whole
 * values render exactly as they did before.
 *
 * Bryntum's editor text does NOT come from here: DurationField renders
 * it through `Duration.toString()`, which hard-codes a one-decimal
 * number format. `installBryntumDurationFormat` (bryntumDurationFormat.js,
 * called from index.js) re-assigns that method with a mask derived from this
 * constant, so the editor follows whatever precision the cells use.
 */
export const DISPLAY_DECIMALS = 2;

/**
 * Display rounding of the extended contract: 3 decimals, half away from
 * zero, trailing zeros trimmed, as a string.
 *
 * It rounds the decimal digits, not the binary value: 2.0005 is stored as
 * 2.000499999999999989…, and `Math.round(v * 1000) / 1000` prints "2" where
 * the user typed a value that reads "2.001". `toPrecision(15)` first drops
 * that binary noise — 15 significant digits are exact for every value a
 * Duration, Work or lag field can hold.
 */
export const ROUND_DISPLAY_DECIMALS = 3;

// "0999" → "1000": the carry runs on the digits, so no value is too long
// for a Number on the way.
const incrementDigits = (digits) => {
	const next = digits.split("");
	let i = next.length - 1;
	while (i >= 0 && next[i] === "9") {
		next[i] = "0";
		i -= 1;
	}
	if (i < 0) return `1${next.join("")}`;
	next[i] = String(Number(next[i]) + 1);
	return next.join("");
};

// Number() also reads " " and [] as 0, true as 1 and "0x10" as 16.
const DECIMAL_TEXT = /^[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?$/i;

export const roundDisplay = (value) => {
	if (value == null || typeof value === "boolean" || Array.isArray(value)) return null;
	if (typeof value === "string" && !DECIMAL_TEXT.test(value.trim())) return null;
	const n = Number(value);
	if (!Number.isFinite(n)) return null;
	const abs = Math.abs(n);
	if (abs >= 1e15) return String(n);
	if (abs < 1e-6) return "0";
	const precise = abs.toPrecision(15);
	// 999999999999999.9 rounds up to 15 significant digits as "1.00…e+15".
	if (precise.includes("e")) return `${n < 0 ? "-" : ""}${Number(precise)}`;
	const [whole, decimals = ""] = precise.split(".");
	const kept = decimals.slice(0, ROUND_DISPLAY_DECIMALS).padEnd(ROUND_DISPLAY_DECIMALS, "0");
	let text = whole + kept;
	if (decimals.charAt(ROUND_DISPLAY_DECIMALS) >= "5") text = incrementDigits(text);
	if (/^0+$/.test(text)) return "0";
	const cut = text.length - ROUND_DISPLAY_DECIMALS;
	const fraction = text.slice(cut).replace(/0+$/, "");
	return `${n < 0 ? "-" : ""}${text.slice(0, cut)}${fraction ? `.${fraction}` : ""}`;
};

const UNIT_SUFFIXES = {
	month: ["mo", "mois", "month", "months"],
	week: ["w", "sem", "week", "weeks", "semaine", "semaines"],
	day: ["d", "j", "day", "days", "jour", "jours"],
	hour: ["h", "hour", "hours", "heure", "heures"],
};

const UNIT_BY_SUFFIX = Object.entries(UNIT_SUFFIXES).reduce((bySuffix, [unit, suffixes]) => {
	suffixes.forEach((suffix) => {
		bySuffix[suffix] = unit;
	});
	return bySuffix;
}, {});

const UNIT_INPUT = /^(-?)(\d+\.?\d{0,3}|\.\d{1,3})\s*([a-z]*)$/;

const activeUnit = (dimension, settings) =>
	dimension === "work" ? settings.workUnit : settings.durationUnit;

const toCore = (magnitude, dimension, settings) =>
	dimension === "work"
		? fromDisplayWork(magnitude, undefined, settings)
		: fromDisplayDuration(magnitude, undefined, settings);

/**
 * The one input grammar of every Duration, Work and lag field: a number
 * with a dot and at most three decimals, alone or followed by the active
 * unit's suffix (en or fr). Any other unit is refused rather than
 * converted, so "44d" typed in a Month portfolio is an error, not 2 months.
 *
 * @returns {{core: number} | {error: "otherUnit" | "invalid"}} core is in
 *          storage units (days / hours), unrounded.
 */
export const parseUnitInput = (text, dimension, { allowNegative = false, settings = cache } = {}) => {
	const match = UNIT_INPUT.exec(String(text ?? "").trim().toLowerCase());
	if (!match) return { error: "invalid" };
	const [, sign, number, suffix] = match;
	if (sign && !allowNegative) return { error: "invalid" };
	if (suffix) {
		const unit = UNIT_BY_SUFFIX[suffix];
		if (!unit) return { error: "invalid" };
		if (unit !== activeUnit(dimension, settings)) return { error: "otherUnit" };
	}
	return { core: toCore(Number(sign + number), dimension, settings) };
};

/** The refusal text for a `parseUnitInput` / `parseDependencyText` error. */
export const unitInputMessage = (error, dimension, { allowNegative = false, settings = cache } = {}) => {
	if (error === "otherUnit") {
		const unit = i18n.t(`ganttUnits.${activeUnit(dimension, settings)}s`);
		return i18n.t("ganttUnits.input.otherUnit", { unit });
	}
	return i18n.t(allowNegative ? "ganttUnits.input.invalidSigned" : "ganttUnits.input.invalid");
};

/**
 * What an editor does with its text on commit: `skip` when the typed value
 * reads like the stored one at display rounding — "4.545" over a 100-day
 * duration is the value the user was shown, not a change to 99.99 days.
 */
export const resolveUnitEdit = ({ text, dimension, storedCore, allowNegative = false, settings = cache }) => {
	const parsed = parseUnitInput(text, dimension, { allowNegative, settings });
	if (parsed.error) {
		return { action: "refuse", message: unitInputMessage(parsed.error, dimension, { allowNegative, settings }) };
	}
	const toDisplay = dimension === "work" ? toDisplayWork : toDisplayDuration;
	if (storedCore != null && roundDisplay(toDisplay(parsed.core, settings)) === roundDisplay(toDisplay(storedCore, settings))) {
		return { action: "skip" };
	}
	return { action: "commit", core: parsed.core };
};

/**
 * A dependency lag as the backend stores it: exact days, signed, inside the
 * ±1000-day bound. Never rounded — the backend rounds for scheduling itself.
 * `lagUnit` is the unit the lag is held in on the record ("day" once loaded,
 * the display unit after a pre-flag edit); omitted means days.
 */
export const lagDaysForRequest = (lag, lagUnit, settings = cache) => {
	return clampLagDays(fromDisplayDuration(lag ?? 0, lagUnit || DURATION_UNITS.DAY, settings));
};

/** The active duration unit's suffix ("mo", "mois"), as cells and inputs print it. */
export const durationSuffix = (settings = cache) => labelsFor(settings.durationUnit).short;

const lagBoundDisplay = (settings) => Number(roundDisplay(toDisplayDuration(MAX_LAGS, settings)));

/**
 * The refusal for a lag beyond the ±1000-day bound, stated in the display
 * unit; null within it. The bound is judged as displayed: "45.455" months is
 * the bound the message names, so it is accepted (and stored as 1000 days)
 * although 45.455 × 22 is 1000.01.
 */
export const lagOutOfRangeMessage = (days, settings = cache) => {
	const bound = lagBoundDisplay(settings);
	if (Math.abs(Number(roundDisplay(toDisplayDuration(days, settings)))) <= bound) return null;
	return i18n.t("ganttUnits.input.lagOutOfRange", {
		min: roundDisplay(-bound),
		max: roundDisplay(bound),
		unit: durationSuffix(settings),
	});
};

/** A lag inside the bound, in days; the displayed bound itself lands on it exactly. */
export const clampLagDays = (days) => Math.max(MIN_LAGS, Math.min(MAX_LAGS, days));

/**
 * `resolveUnitEdit` for a lag field: a duration that may be negative, refused
 * beyond the ±1000-day bound rather than clamped on save.
 */
export const resolveLagEdit = ({ text, storedDays, settings = cache }) => {
	const result = resolveUnitEdit({ text, dimension: "duration", storedCore: storedDays, allowNegative: true, settings });
	if (result.action !== "commit") return result;
	const message = lagOutOfRangeMessage(result.core, settings);
	if (message) return { action: "refuse", message };
	return { action: "commit", core: clampLagDays(result.core) };
};

const KEYSTROKE_NUMBER = /^\s*-?\s*\d*(?:\.(\d*))?/;

/**
 * Keystroke filter of the unit input grammar. `currentText` is the text
 * before the caret, `insertedText` the keystroke followed by whatever
 * already sits after the caret. False blocks the keystroke.
 */
export const sanitizeUnitKeystroke = (currentText, insertedText, { allowNegative = false } = {}) => {
	const inserted = String(insertedText ?? "");
	const candidate = `${currentText ?? ""}${inserted}`;
	if (inserted.includes(",")) return false;
	if (inserted.includes("-")) {
		const leading = candidate.trimStart();
		if (!allowNegative || leading.indexOf("-") !== 0 || leading.lastIndexOf("-") !== 0) return false;
	}
	if (inserted.includes(".") && candidate.split(".").length > 2) return false;
	const decimals = KEYSTROKE_NUMBER.exec(candidate)[1];
	return !decimals || decimals.length <= ROUND_DISPLAY_DECIMALS;
};

const DEPENDENCY_LINK =/^(\d+)\s*(fs|ss|ff|sf)?\s*(?:([+-])\s*(.+))?$/i;

/**
 * Predecessors / Successors notation: `<ref><type?><sign><lag><suffix?>`
 * separated by ";". The type defaults to FS; the lag follows the same unit
 * rule as every other field.
 *
 * @returns {{links: {ref: string, type: string, lagDays: number}[]} | {error: "otherUnit" | "invalid"}}
 */
export const parseDependencyText = (text, { settings = cache } = {}) => {
	const trimmed = String(text ?? "").trim();
	if (!trimmed) return { links: [] };
	const links = [];
	const parts = trimmed.split(";");
	if (parts.length > 1 && !parts[parts.length - 1].trim()) parts.pop();
	for (const part of parts) {
		const match = DEPENDENCY_LINK.exec(part.trim());
		if (!match) return { error: "invalid" };
		const [, ref, type, sign, lagText] = match;
		let lagDays = 0;
		if (sign) {
			const lag = parseUnitInput(lagText, "duration", { settings });
			if (lag.error) return { error: lag.error };
			lagDays = sign === "-" && lag.core !== 0 ? -lag.core : lag.core;
		}
		links.push({ ref, type: (type || "FS").toUpperCase(), lagDays });
	}
	return { links };
};

/** `{ref: "25388", type: "FF", lagDays: -10}` → "25388FF-0.455mo" (Month, 22 days). */
export const formatDependencyLink = ({ ref, type, lagDays }, settings = cache) => {
	const typeText = !type || type === "FS" ? "" : type;
	const lagText = roundDisplay(toDisplayDuration(lagDays || 0, settings));
	if (!lagText || lagText === "0") return `${ref}${typeText}`;
	const sign = lagText.startsWith("-") ? "" : "+";
	return `${ref}${typeText}${sign}${lagText}${labelsFor(settings.durationUnit).short}`;
};

/**
 * A Duration or Work value in the portfolio's unit. `short` is the cell,
 * input, tooltip and chart form ("0.455 mo"); `long` is the configuration
 * and message form, singular up to one ("0.5 month", "2 months").
 */
export const formatUnitValue = (coreValue, dimension, { style = "short", settings = cache } = {}) => {
	const isWork = dimension === "work";
	const display = isWork ? toDisplayWork(coreValue, settings) : toDisplayDuration(coreValue, settings);
	const text = roundDisplay(display);
	if (text == null) return "";
	const labels = labelsFor(activeUnit(dimension, settings));
	if (style === "short") return `${text} ${labels.short}`;
	return `${text} ${Math.abs(Number(text)) <= 1 ? labels.one : labels.many}`;
};

const toDisplayFor = (coreValue, dimension, settings) =>
	dimension === "work" ? toDisplayWork(coreValue, settings) : toDisplayDuration(coreValue, settings);

/** The active unit's abbreviation for a dimension: "mo", "d", "h". */
export const unitSuffix = (dimension, settings = cache) => labelsFor(activeUnit(dimension, settings)).short;

/** Excel cell number: the display value at display rounding; null when missing. */
export const excelUnitValue = (coreValue, dimension, settings = cache) => {
	const text = roundDisplay(toDisplayFor(coreValue, dimension, settings));
	return text == null ? null : Number(text);
};

/** "Work  (At completion) " → "Work (At completion) (d)". */
export const unitHeader = (label, dimension, settings = cache) => {
	const base = String(label ?? "").replace(/\s+/g, " ").trim();
	return `${base} (${unitSuffix(dimension, settings)})`;
};

/** A signed delta in the display unit: "+0.455 mo", "-2 mo", "0 mo". */
export const formatSignedUnitValue = (coreValue, dimension, settings = cache) => {
	const text = roundDisplay(toDisplayFor(coreValue, dimension, settings));
	if (text == null) return "";
	const sign = text === "0" || text.startsWith("-") ? "" : "+";
	return `${sign}${text} ${unitSuffix(dimension, settings)}`;
};

/** Hours to man-days with the hours per day Work = Day uses; null when unknown. */
export const toMandays = (hours, settings = cache) => {
	if (hours == null || hours === "" || !(settings.hoursPerDay > 0)) return null;
	return hours / settings.hoursPerDay;
};

export const mandaysLabel = (hours, settings = cache) => roundDisplay(toMandays(hours, settings)) ?? "";

/** Chart axis / tooltip label for a value already in the display unit. */
export const unitAxisLabel = (value) => roundDisplay(value) ?? "";

/** The precision in force: 3 under the extended contract, 2 otherwise. */
export const displayDecimals = (settings = cache) =>
	isExtendedDisplay(settings) ? ROUND_DISPLAY_DECIMALS : DISPLAY_DECIMALS;

export const roundDisplayMagnitude = (value, settings = cache) =>
	isExtendedDisplay(settings)
		? Number(roundDisplay(value))
		: roundToDecimalPlace(value, DISPLAY_DECIMALS);

const formatMagnitude = (value, settings = cache) => {
	if (value == null || !Number.isFinite(Number(value))) return null;
	return isExtendedDisplay(settings)
		? roundDisplay(value)
		: String(roundToDecimalPlace(Number(value), DISPLAY_DECIMALS));
};

const withUnitLabel = (text, unit, settings = cache) => {
	const labels = labelsFor(unit);
	if (isExtendedDisplay(settings)) return `${text} ${labels.short}`;
	const label = text === "1" || text === "-1" ? labels.one : labels.many;
	return `${text} ${label}`;
};

// The cell text of an already rounded magnitude, grouped in the UI language
// ("12,345.5" / "12 345,5"). Rounding stays formatMagnitude's.
const groupedMagnitude = (text, settings) =>
	text == null ? null : formatGroupedNumber(Number(text), undefined, displayDecimals(settings));

/** 12 → "12 days" (DAY) / "2.4 weeks" (WEEK, daysPerWeek 5); 12345.5 → "12,345.5 days". */
export const formatDuration = (days, settings = cache) => {
	const text = groupedMagnitude(formatMagnitude(toDisplayDuration(days, settings), settings), settings);
	return text == null ? "" : withUnitLabel(text, settings.durationUnit, settings);
};

/** 40 → "40 hours" (HOUR) / "5 days" (DAY, hoursPerDay 8); 12345.5 → "12,345.5 hours". */
export const formatWork = (hours, settings = cache) => {
	const text = groupedMagnitude(formatMagnitude(toDisplayWork(hours, settings), settings), settings);
	return text == null ? "" : withUnitLabel(text, settings.workUnit, settings);
};

const CONVERTIBLE_LAG_UNITS = new Set([
	DURATION_UNITS.DAY,
	DURATION_UNITS.WEEK,
	DURATION_UNITS.MONTH,
]);

/**
 * Compact lag for the Predecessors/Successors cell notation
 * ("12SS+2.4w"). Mirrors Bryntum's DependencyBaseModel.getLag(): empty
 * string when there is no lag, explicit sign otherwise. Outside the
 * extended contract a lag on a core portfolio, or in a unit the
 * working-time factors don't cover, prints its raw magnitude — an hour lag
 * stays "+4h" exactly as getLag() printed it. Under the extended contract
 * every magnitude goes through roundDisplay, and a lag that rounds to zero
 * prints nothing, as formatDependencyLink does.
 */
export const formatLag = (lag, lagUnit, settings = cache) => {
	if (!lag) return "";
	const unit = lagUnit || DURATION_UNITS.DAY;
	const extended = isExtendedDisplay(settings);
	// Core portfolio, or a unit the working-time factors don't cover: the
	// lag's OWN unit suffix — byte-identical to the replaced
	// dependency.getLag() ("+2w" stays "+2w" on a DAY portfolio).
	if (isDurationCore(settings) || !CONVERTIBLE_LAG_UNITS.has(unit)) {
		if (!extended) return `${lag >= 0 ? "+" : ""}${lag}${labelsFor(unit).short}`;
		const text = roundDisplay(lag);
		if (text === "0") return "";
		return `${text.startsWith("-") ? "" : "+"}${text}${labelsFor(unit).short}`;
	}
	const lagDays = lag * factorForDurationUnit(unit, settings);
	const text = formatMagnitude(toDisplayDuration(lagDays, settings), settings);
	if (extended && text === "0") return "";
	return `${Number(text) >= 0 ? "+" : ""}${text}${labelsFor(settings.durationUnit).short}`;
};

/**
 * Editor `allowedUnits`: active display unit first so a bare number
 * ("2.5") parses in the active unit; day always allowed as an escape
 * hatch for precise input.
 */
export const durationEditorUnits = (settings = cache) => {
	if (settings.durationUnit === DURATION_UNITS.DAY) return ["day"];
	return isExtendedDisplay(settings) ? [settings.durationUnit] : [settings.durationUnit, "day"];
};

export const workEditorUnits = (settings = cache) => {
	if (settings.workUnit === WORK_UNITS.HOUR) return ["hour"];
	return isExtendedDisplay(settings) ? ["day"] : ["day", "hour"];
};

/**
 * Editor bounds must be expressed in the active unit: Bryntum compares
 * min/max in *calendar* milliseconds (week = 7×24 h), so "1200 days"
 * would wrongly block "240 weeks" (= 1200 working days). The reverse
 * hole (day-united input passing a week bound's calendar-ms value) is
 * closed by day-based clamps in the parse-back paths.
 */
export const durationEditorBound = (days, settings = cache) => {
	const magnitude = roundDisplayMagnitude(toDisplayDuration(days, settings), settings);
	return `${magnitude} ${settings.durationUnit}s`;
};

export const workEditorBound = (hours, settings = cache) => {
	const magnitude = roundDisplayMagnitude(toDisplayWork(hours, settings), settings);
	return `${magnitude} ${settings.workUnit}s`;
};

const compare = (left, right, operator) => {
	switch (operator) {
		case "=":
			return left === right;
		case "!=":
			return left !== right;
		case "<":
			return left < right;
		case "<=":
			return left <= right;
		case ">":
			return left > right;
		case ">=":
			return left >= right;
		default:
			return left === right;
	}
};

const typedToCore = (typed, dimension, settings) => {
	// Bryntum duration/effort filter fields produce {magnitude, unit};
	// honor an explicitly typed unit ("10 days" while WEEK is active).
	// Plain numbers mean the active display unit.
	if (typed && typeof typed === "object" && typed.magnitude != null) {
		const magnitude = Number(typed.magnitude);
		if (!Number.isFinite(magnitude)) return null;
		return dimension === "work"
			? fromDisplayWork(magnitude, typed.unit, settings)
			: fromDisplayDuration(magnitude, typed.unit, settings);
	}
	const n = Number(typed);
	if (!Number.isFinite(n)) return null;
	return dimension === "work"
		? fromDisplayWork(n, undefined, settings)
		: fromDisplayDuration(n, undefined, settings);
};

/**
 * Filter comparison in display space, rounded to what the user actually
 * sees (DISPLAY_DECIMALS): a cell showing "2.4 weeks" must match a filter of
 * 2.4 even though the raw quotient is 2.4000000003. Nullish record
 * values never match (mirrors Bryntum's default nullish handling) —
 * except "!=", where an absent value is indeed "not equal".
 * `dimension` is "duration" | "work".
 */
export const compareInDisplayUnit = (coreValue, typedValue, operator, dimension, settings = cache) => {
	const typedCore = typedToCore(typedValue, dimension, settings);
	if (typedCore == null) return true;
	if (coreValue == null) return operator === "!=";
	const toDisplay = dimension === "work" ? toDisplayWork : toDisplayDuration;
	const recordDisplay = roundDisplayMagnitude(toDisplay(coreValue, settings), settings);
	const typedDisplay = roundDisplayMagnitude(toDisplay(typedCore, settings), settings);
	return compare(recordDisplay, typedDisplay, operator);
};

/**
 * Preset a Bryntum Duration/Effort editor field to the display unit so
 * what the user sees, edits and types (bare numbers) is the active
 * unit.
 *
 * The magnitude is rounded to DISPLAY_DECIMALS — the same precision the
 * cell renders and the field displays — so the value survives the
 * round-trip through the input text unchanged. A finer magnitude (this
 * used to preset 4 decimals) can never equal what the displayed text
 * re-parses to, which defeats `rebaseBaseline` in Gantt.js: Bryntum then
 * reads every close as dirty and commits a value the user never typed.
 */
export const presetUnitField = (field, coreValue, dimension, settings = cache) => {
	if (!field || coreValue == null) return;
	const isWork = dimension === "work";
	field.decimalPrecision = displayDecimals(settings);
	field.value = {
		magnitude: roundDisplayMagnitude(
			isWork ? toDisplayWork(coreValue, settings) : toDisplayDuration(coreValue, settings),
			settings
		),
		unit: isWork ? settings.workUnit : settings.durationUnit,
	};
};

/**
 * Restore core units on a record that an editor preset or dialog field
 * left week/month (or work-day) united. Same span, day/hour united —
 * every legacy consumer and DTO builder assumes core units at rest.
 *
 * The two axes need different write shapes, which is not symmetry anyone
 * would choose: `fullDuration` is a real field on the Bryntum task model and
 * `record.set` reaches it, while `fullEffort` is only an accessor pair over
 * `effort`/`effortUnit`. A `set("fullEffort", …)` is therefore dropped in
 * silence — measured against a real Bryntum Gantt: after a work-day-united
 * commit the record stayed `5 day` whatever magnitude was written, including a
 * deliberately wrong one, while `set({ effort, effortUnit })` moved it. It is
 * also why Bryntum's own editor teardown calls the `setFullEffort` method
 * rather than the generic field setter (Editor#onEditComplete).
 *
 * So the effort branch below is not interchangeable with the duration one, and
 * a test pins its shape: the Jest suite stubs `@bryntum/*`, so nothing else
 * would notice it going back to a no-op.
 */
export const normalizeRecordToCoreUnits = (record) => {
	if (!record) return;
	if (record.durationUnit && record.durationUnit !== "day" && record.duration != null) {
		record.set("fullDuration", {
			magnitude: fromDisplayDuration(record.duration, record.durationUnit),
			unit: "day",
		});
	}
	if (record.effortUnit && record.effortUnit !== "hour" && record.effort != null) {
		record.set({
			effort: fromDisplayWork(record.effort, record.effortUnit),
			effortUnit: "hour",
		});
	}
};

export const _resetDisplayUnitSettingsForTests = () => {
	Object.assign(cache, DEFAULTS);
	cache.labels = null;
};

/**
 * A lag held in days (My Knowledge dependencies, block lags, inter-scope
 * rules) in the portfolio's duration unit: compact next to a link type
 * ("+2mo", as the dependency notation prints it), spaced on its own
 * ("+0.455 mo"). Zero prints "0mo" / "0 mo".
 */
export const lagLabel = (days, { spaced = false, settings = cache } = {}) => {
	const lag = Number(days) || 0;
	if (spaced) return formatSignedUnitValue(lag, "duration", settings);
	return (lag && formatLag(lag, DURATION_UNITS.DAY, settings)) || `0${durationSuffix(settings)}`;
};
